/**
 * Date and Time Formatting Utilities
 * Handles timezone-aware date formatting with Workday fallback
 */

import { workdayAPI } from '../services/api';

// Cache for user timezone data
let userTimezoneCache = null;
let workdayTimezoneCache = {};

/**
 * Get user's timezone from browser or Workday
 * @param {string} userEmail - User's email for Workday lookup
 * @returns {Promise<string>} Timezone string (e.g., "America/Los_Angeles")
 */
export const getUserTimezone = async (userEmail = null) => {
  // Try browser timezone first (most accurate)
  try {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (browserTimezone) {
      console.log('✅ Using browser timezone:', browserTimezone);
      return browserTimezone;
    }
  } catch (error) {
    console.warn('⚠️ Browser timezone not available:', error);
  }

  // Fallback to Workday location-based timezone
  if (userEmail && !workdayTimezoneCache[userEmail]) {
    try {
      const workdayData = await workdayAPI.getUserData(userEmail);
      const timezone = mapLocationToTimezone(workdayData);
      workdayTimezoneCache[userEmail] = timezone;
      console.log('✅ Using Workday timezone:', timezone);
      return timezone;
    } catch (error) {
      console.warn('⚠️ Workday timezone lookup failed:', error);
    }
  }

  // Return cached Workday timezone if available
  if (workdayTimezoneCache[userEmail]) {
    return workdayTimezoneCache[userEmail];
  }

  // Final fallback to UTC
  console.warn('⚠️ Using fallback timezone: UTC');
  return 'UTC';
};

/**
 * Map Workday location (city, state) to timezone
 * @param {Object} workdayData - Workday user data
 * @returns {string} Timezone string
 */
const mapLocationToTimezone = (workdayData) => {
  const state = workdayData?.state || '';
  const city = workdayData?.city || '';
  const country = workdayData?.PrimaryWorkCountry || 'United States';

  // US state to timezone mapping
  const usTimezones = {
    // Pacific
    'CA': 'America/Los_Angeles', 'WA': 'America/Los_Angeles', 'OR': 'America/Los_Angeles',
    'NV': 'America/Los_Angeles',
    // Mountain
    'MT': 'America/Denver', 'CO': 'America/Denver', 'UT': 'America/Denver',
    'WY': 'America/Denver', 'NM': 'America/Denver', 'AZ': 'America/Phoenix',
    // Central
    'TX': 'America/Chicago', 'IL': 'America/Chicago', 'MN': 'America/Chicago',
    'WI': 'America/Chicago', 'MO': 'America/Chicago', 'IA': 'America/Chicago',
    // Eastern
    'NY': 'America/New_York', 'FL': 'America/New_York', 'MA': 'America/New_York',
    'PA': 'America/New_York', 'NC': 'America/New_York', 'GA': 'America/New_York',
  };

  // Check US states
  if (country === 'United States' && usTimezones[state]) {
    return usTimezones[state];
  }

  // Other countries
  const countryTimezones = {
    'United Kingdom': 'Europe/London',
    'India': 'Asia/Kolkata',
    'China': 'Asia/Shanghai',
    'Japan': 'Asia/Tokyo',
    'Australia': 'Australia/Sydney',
  };

  return countryTimezones[country] || 'UTC';
};

/**
 * Get timezone abbreviation (PST, EST, etc.)
 * @param {string} timezone - Timezone string
 * @returns {string} Timezone abbreviation
 */
export const getTimezoneAbbreviation = (timezone = null) => {
  try {
    const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const date = new Date();
    const formatted = date.toLocaleTimeString('en-US', {
      timeZone: tz,
      timeZoneName: 'short'
    });
    return formatted.split(' ').pop(); // Extract "PST", "EST", etc.
  } catch (error) {
    return 'UTC';
  }
};

/**
 * Parse a backend timestamp.
 * The API serialises naive UTC (`datetime.utcnow().isoformat()`), and JS reads a
 * date-time without a designator as local time, so UTC has to be made explicit.
 * @param {string|Date} value
 * @returns {Date}
 */
export const parseTimestamp = (value) => {
  if (value instanceof Date) return value;

  const raw = String(value ?? '').trim();
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  if (!raw.includes('T') || hasZone) return new Date(raw);

  return new Date(`${raw}Z`);
};

/**
 * Format timestamp with timezone awareness
 * @param {string} isoTimestamp - ISO timestamp string
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date/time string
 */
export const formatTimestamp = async (isoTimestamp, options = {}) => {
  if (!isoTimestamp) return '';

  const {
    showTime = true,
    showRelative = false,
    showTimezone = false,
    userEmail = null,
    timezone = null,
  } = options;

  const date = parseTimestamp(isoTimestamp);
  const userTimezone = timezone || await getUserTimezone(userEmail);

  // Relative time (e.g., "5m ago", "2h ago")
  if (showRelative) {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
  }

  // Full timestamp
  const formatted = date.toLocaleString('en-US', {
    timeZone: userTimezone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(showTime && {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  });

  // Add timezone abbreviation
  if (showTimezone && showTime) {
    const tzAbbr = getTimezoneAbbreviation(userTimezone);
    return `${formatted} ${tzAbbr}`;
  }

  return formatted;
};

export default {
  getUserTimezone,
  getTimezoneAbbreviation,
  formatTimestamp,
  parseTimestamp,
};
